import React, { useEffect, useState } from 'react';
import { Input, Select, Spinner } from '@veda/dashboard-ui';
import { MdAccountBalance, MdEdit, MdSave, MdClose } from 'react-icons/md';
import { usePaApi } from '../services/paApi';
import { useAuthUser } from '../../ppp/hooks/useAuthUser';

const TIPOLOGIA_OPTIONS = [
  'Comune', 'Provincia', 'Regione', 'Città Metropolitana',
  'ASL', 'Università', 'Ente Pubblico Non Economico', 'Altro',
].map((t) => ({ value: t, label: t }));

export default function MioEntePage() {
  const api = usePaApi();
  const { can } = useAuthUser();
  const [ente,      setEnte]      = useState(null);
  const [form,      setForm]      = useState(null);
  const [modifica,  setModifica]  = useState(false);
  const [salvando,  setSalvando]  = useState(false);
  const [msg,       setMsg]       = useState('');

  useEffect(() => {
    api.getMioEnte({
      onSuccess: (data) => { setEnte(data); setForm(data); },
    });
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const set = (key) => (e) => setForm((p) => ({ ...p, [key]: e.target.value }));

  const handleSalva = () => {
    setSalvando(true);
    api.aggiornaMioEnte({
      denominazione:  form.denominazione,
      codiceIpa:      form.codiceIpa,
      tipologia:      form.tipologia,
      indirizzo:      form.indirizzo,
      comune:         form.comune,
      provincia:      form.provincia,
      cap:            form.cap,
      pec:            form.pec,
      sitoWeb:        form.sitoWeb,
      referenteNome:  form.referenteNome,
      referenteEmail: form.referenteEmail,
      attivo:         form.attivo ?? true,
    }, {
      onSuccess: () => {
        setEnte(form);
        setModifica(false);
        setMsg('Dati aggiornati con successo.');
        setSalvando(false);
        setTimeout(() => setMsg(''), 3000);
      },
      onFormError: () => setSalvando(false),
    });
  };

  if (api.loading && !ente) {
    return (
      <div className="flex items-center justify-center py-20">
        <Spinner />
      </div>
    );
  }

  if (!ente) {
    return (
      <div className="w-full py-8 px-4 sm:px-6 lg:px-8">
        <div className="flex items-center gap-3 mb-6">
          <div className="w-10 h-10 rounded-xl bg-blue-100 dark:bg-blue-900 flex items-center justify-center text-blue-600 dark:text-blue-400">
            <MdAccountBalance size={22} />
          </div>
          <h1 className="text-xl font-bold text-gray-800 dark:text-white">Il mio ente</h1>
        </div>
        <div className="bg-amber-50 border border-amber-200 rounded-xl p-6 text-amber-800 text-sm">
          Impossibile caricare i dati dell'ente. Riprova o contatta il supporto se il problema persiste.
        </div>
      </div>
    );
  }

  return (
    <div className="w-full py-8 px-4 sm:px-6 lg:px-8">
      {/* Header */}
      <div className="flex items-center justify-between mb-6">
        <div className="flex items-center gap-3">
          <div className="w-10 h-10 rounded-xl bg-blue-100 dark:bg-blue-900 flex items-center justify-center text-blue-600 dark:text-blue-400">
            <MdAccountBalance size={22} />
          </div>
          <div>
            <h1 className="text-xl font-bold text-gray-800 dark:text-white">Il mio ente</h1>
            <p className="text-sm text-gray-500 dark:text-gray-400">{ente?.tipologia ?? 'Ente pubblico'}</p>
          </div>
        </div>
        {!modifica && can('ppp.ente.edit') && (
          <button
            onClick={() => { setForm(ente); setModifica(true); }}
            className="inline-flex items-center gap-1.5 px-4 py-2 rounded-lg text-sm font-medium bg-blue-600 hover:bg-blue-700 text-white transition-colors"
          >
            <MdEdit size={16} /> Modifica
          </button>
        )}
        {modifica && (
          <div className="flex gap-2">
            <button
              onClick={() => { setForm(ente); setModifica(false); }}
              className="inline-flex items-center gap-1.5 px-4 py-2 rounded-lg text-sm font-medium border border-gray-200 dark:border-gray-600 text-gray-600 dark:text-gray-300 hover:bg-gray-50 dark:hover:bg-gray-700 transition-colors"
            >
              <MdClose size={16} /> Annulla
            </button>
            <button
              onClick={handleSalva}
              disabled={salvando}
              className="inline-flex items-center gap-1.5 px-4 py-2 rounded-lg text-sm font-medium bg-green-600 hover:bg-green-700 disabled:opacity-60 text-white transition-colors"
            >
              <MdSave size={16} /> {salvando ? 'Salvataggio...' : 'Salva'}
            </button>
          </div>
        )}
      </div>

      {msg && (
        <div className="mb-4 p-3 rounded-xl bg-green-50 dark:bg-green-900/20 text-green-700 dark:text-green-300 text-sm">
          {msg}
        </div>
      )}

      {/* Form / vista */}
      <div className="bg-white dark:bg-gray-800 rounded-2xl shadow-sm border border-gray-100 dark:border-gray-700 p-8 lg:p-10">
        <div className="max-w-4xl space-y-5">
        {/* Anagrafica */}
        <div className="grid grid-cols-1 gap-4">
          {modifica && form ? (
            <Input label="Denominazione *" value={form.denominazione ?? ''} onChange={set('denominazione')} />
          ) : (
            <div>
              <p className="text-xs font-medium text-gray-500 uppercase tracking-wide">Denominazione</p>
              <p className="text-lg font-semibold text-gray-800 dark:text-white mt-0.5">{ente?.denominazione ?? '—'}</p>
            </div>
          )}
        </div>

        <div className="grid grid-cols-2 gap-4">
          {modifica ? (
            <>
              <Select label="Tipologia" value={form.tipologia ?? ''} onChange={set('tipologia')} options={TIPOLOGIA_OPTIONS} />
              <Input label="Codice IPA" value={form.codiceIpa ?? ''} onChange={set('codiceIpa')} />
            </>
          ) : (
            <>
              <div>
                <p className="text-xs font-medium text-gray-500 uppercase tracking-wide">Tipologia</p>
                <p className="text-gray-800 dark:text-white mt-0.5">{ente?.tipologia ?? '—'}</p>
              </div>
              <div>
                <p className="text-xs font-medium text-gray-500 uppercase tracking-wide">Codice IPA</p>
                <p className="text-gray-800 dark:text-white mt-0.5 font-mono text-sm">{ente?.codiceIpa ?? '—'}</p>
              </div>
            </>
          )}
        </div>

        <hr className="border-gray-100 dark:border-gray-700" />

        {/* Sede */}
        <p className="text-xs font-medium text-gray-500 uppercase tracking-wide">Sede</p>
        <div className="grid grid-cols-2 gap-4">
          {modifica ? (
            <>
              <Input label="Indirizzo" value={form.indirizzo ?? ''} onChange={set('indirizzo')} />
              <Input label="Comune" value={form.comune ?? ''} onChange={set('comune')} />
              <Input label="Provincia (2 lett.)" value={form.provincia ?? ''} onChange={set('provincia')} />
              <Input label="CAP" value={form.cap ?? ''} onChange={set('cap')} />
            </>
          ) : (
            <>
              <div>
                <p className="text-xs font-medium text-gray-500 uppercase tracking-wide">Indirizzo</p>
                <p className="text-gray-800 dark:text-white mt-0.5">{ente?.indirizzo ?? '—'}</p>
              </div>
              <div>
                <p className="text-xs font-medium text-gray-500 uppercase tracking-wide">Comune</p>
                <p className="text-gray-800 dark:text-white mt-0.5">{ente?.comune} {ente?.provincia ? `(${ente.provincia})` : ''}</p>
              </div>
            </>
          )}
        </div>

        <hr className="border-gray-100 dark:border-gray-700" />

        {/* Contatti */}
        <p className="text-xs font-medium text-gray-500 uppercase tracking-wide">Contatti</p>
        <div className="grid grid-cols-1 gap-4">
          {modifica ? (
            <div className="grid grid-cols-2 gap-4">
              <Input label="PEC" type="email" value={form.pec ?? ''} onChange={set('pec')} />
              <Input label="Sito web" value={form.sitoWeb ?? ''} onChange={set('sitoWeb')} />
              <Input label="Nome referente" value={form.referenteNome ?? ''} onChange={set('referenteNome')} />
              <Input label="Email referente" type="email" value={form.referenteEmail ?? ''} onChange={set('referenteEmail')} />
            </div>
          ) : (
            <div className="grid grid-cols-2 gap-4">
              <div>
                <p className="text-xs font-medium text-gray-500 uppercase tracking-wide">PEC</p>
                <p className="text-gray-800 dark:text-white mt-0.5 text-sm">{ente?.pec ?? '—'}</p>
              </div>
              <div>
                <p className="text-xs font-medium text-gray-500 uppercase tracking-wide">Sito web</p>
                <p className="text-gray-800 dark:text-white mt-0.5 text-sm">{ente?.sitoWeb ?? '—'}</p>
              </div>
              <div>
                <p className="text-xs font-medium text-gray-500 uppercase tracking-wide">Referente</p>
                <p className="text-gray-800 dark:text-white mt-0.5">{ente?.referenteNome ?? '—'}</p>
              </div>
              <div>
                <p className="text-xs font-medium text-gray-500 uppercase tracking-wide">Email referente</p>
                <p className="text-gray-800 dark:text-white mt-0.5 text-sm">{ente?.referenteEmail ?? '—'}</p>
              </div>
            </div>
          )}
        </div>
      </div>
      </div>
    </div>
  );
}
